/**
 * Audio Service
 * Handles API calls for audio track management
 */

import { api } from '@/lib/axios';
import type {
  AudioTrack,
  CreateAudioTrackRequest,
  UpdateAudioTrackRequest,
  AudioTrackFilters,
  AudioSelection,
  AudioCatalogResponse,
} from '@/types/audio';

export const audioService = {
  /**
   * Get all audio tracks (superadmin)
   */
  async getAudioTracks(filters?: AudioTrackFilters): Promise<{ audio_tracks: AudioTrack[] }> {
    const params = new URLSearchParams();

    if (filters?.genre) {
      params.append('genre', filters.genre);
    }

    if (filters?.mood) {
      params.append('mood', filters.mood);
    }

    if (filters?.active !== undefined) {
      params.append('active', filters.active.toString());
    }

    const response = await api.get(
      `/v1/superadmin/audio_tracks${params.toString() ? `?${params.toString()}` : ''}`
    );
    return response.data;
  },

  /**
   * Get a single audio track
   */
  async getAudioTrack(id: string): Promise<AudioTrack> {
    const response = await api.get<AudioTrack>(`/v1/superadmin/audio_tracks/${id}`);
    return response.data;
  },

  /**
   * Create a new audio track
   */
  async createAudioTrack(data: CreateAudioTrackRequest): Promise<AudioTrack> {
    const response = await api.post<AudioTrack>('/v1/superadmin/audio_tracks', data);
    return response.data;
  },

  /**
   * Update an audio track
   */
  async updateAudioTrack(id: string, data: UpdateAudioTrackRequest): Promise<AudioTrack> {
    const response = await api.patch<AudioTrack>(`/v1/superadmin/audio_tracks/${id}`, data);
    return response.data;
  },

  /**
   * Delete an audio track
   */
  async deleteAudioTrack(id: string): Promise<void> {
    await api.delete(`/v1/superadmin/audio_tracks/${id}`);
  },

  /**
   * Browse audio catalog (admin)
   */
  async browseCatalog(): Promise<AudioCatalogResponse> {
    const response = await api.get<AudioCatalogResponse>('/v1/admin/audio_catalog');
    return response.data;
  },

  /**
   * Get current audio selection
   */
  async getSelection(): Promise<AudioSelection> {
    const response = await api.get<AudioSelection>('/v1/admin/audio_selection');
    return response.data;
  },

  /**
   * Set audio selection
   */
  async setSelection(audioTrackId: string): Promise<void> {
    await api.put('/v1/admin/audio_selection', { audio_track_id: audioTrackId });
  },

  /**
   * Clear audio selection
   */
  async clearSelection(): Promise<void> {
    await api.delete('/v1/admin/audio_selection');
  },
};
